import {
  collection,
  doc,
  setDoc,
  getDoc,
  getDocs,
  deleteDoc,
  onSnapshot,
  query,
  where,
  orderBy,
  Unsubscribe,
} from 'firebase/firestore';
import { auth, db } from '../firebase';
import { SongTrack } from '../types';
import { get, set, del } from 'idb-keyval';
import { isUserAdminSync } from './authService';

const MUSIC_COLLECTION_REF = collection(db, 'music_tracks');
const CHUNK_SIZE = 400 * 1024; // 400 KB per chunk (well within Firestore 1MB document limit)

// Memory cache for audio object URLs to avoid redundant reconstruction
const objectUrlCache = new Map<string, string>();

/**
 * Validates that a media URL uses an allowed and safe scheme, blocking javascript: and dangerous payloads.
 */
export function isSafeMediaUrl(url: string): boolean {
  if (!url || typeof url !== 'string') return false;
  const trimmed = url.trim().toLowerCase();
  if (
    trimmed.startsWith('javascript:') ||
    trimmed.startsWith('vbscript:') ||
    trimmed.startsWith('data:text/html') ||
    trimmed.startsWith('data:text/javascript')
  ) {
    return false;
  }
  return (
    trimmed.startsWith('https://') ||
    trimmed.startsWith('http://') ||
    trimmed.startsWith('blob:') ||
    trimmed.startsWith('data:audio/') ||
    trimmed.startsWith('data:image/') ||
    trimmed.startsWith('synth') ||
    trimmed.startsWith('/')
  );
}

/**
 * Computes an isolated, owner-scoped cache key for IndexedDB.
 * Private audio is strictly partitioned by owner UID to prevent unauthorized local cache cross-reads.
 */
function getAudioCacheKey(track: { id: string; isPrivate?: boolean; ownerUid?: string }): string {
  if (track.isPrivate) {
    const owner = track.ownerUid || 'unknown';
    return `couple_audio_priv_${owner}_${track.id}`;
  }
  return `couple_audio_pub_${track.id}`;
}

/**
 * Subscribes to real-time updates for music tracks in Firestore.
 * Implements strictly separated queries matching Firestore security rules:
 * - Admin: Queries all tracks via admin privileges.
 * - Regular authenticated user: Queries public tracks (isPublic == true) + own private tracks (ownerUid == uid).
 * - Visitor: Queries exclusively public tracks (isPublic == true).
 * Ensures no global unconstrained queries are executed that would trigger permission-denied.
 */
export function subscribeToMusicTracks(
  onUpdate: (tracks: SongTrack[]) => void,
  onError?: (err: Error) => void
): Unsubscribe {
  let isCancelled = false;
  let unsubPublic: Unsubscribe | null = null;
  let unsubPrivate: Unsubscribe | null = null;
  let unsubAdmin: Unsubscribe | null = null;

  let publicTracks: SongTrack[] = [];
  let privateTracks: SongTrack[] = [];

  const legacyIds = new Set(['track-1', 'track-2', 'track-3', 'track-4']);

  const parseDoc = (docSnap: any): SongTrack | null => {
    const data = docSnap.data();
    if (data.isDefault || legacyIds.has(docSnap.id)) {
      if (isUserAdminSync(auth.currentUser)) {
        deleteDoc(docSnap.ref).catch(() => {});
      }
      return null;
    }

    return {
      id: docSnap.id,
      title: data.title || 'Nuestra Canción',
      artist: data.artist || '',
      url: data.url || '',
      duration: data.duration || '3:30',
      uploadedBy: data.uploadedBy || 'novio',
      addedAt: data.addedAt || new Date().toISOString(),
      isDefault: false,
      notes: data.notes || '',
      storageType: data.storageType || (data.url?.startsWith('http') || data.url?.startsWith('synth') ? 'url' : 'cloud_chunks'),
      totalChunks: data.totalChunks || 0,
      mimeType: data.mimeType || 'audio/mpeg',
      fileSize: data.fileSize || 0,
      ownerUid: data.ownerUid || '',
      isPublic: data.isPublic !== undefined ? Boolean(data.isPublic) : true,
      isPrivate: data.isPrivate !== undefined ? Boolean(data.isPrivate) : false,
    } as SongTrack;
  };

  const notifyCombined = () => {
    if (isCancelled) return;
    const map = new Map<string, SongTrack>();
    publicTracks.forEach((t) => map.set(t.id, t));
    privateTracks.forEach((t) => map.set(t.id, t));
    const combined = Array.from(map.values()).sort((a, b) =>
      (a.addedAt || '').localeCompare(b.addedAt || '')
    );
    onUpdate(combined);
  };

  const setupAuthSubscriptions = (user: any) => {
    if (isCancelled) return;
    if (unsubPrivate) {
      unsubPrivate();
      unsubPrivate = null;
    }
    if (unsubAdmin) {
      unsubAdmin();
      unsubAdmin = null;
    }
    privateTracks = [];

    const isAdmin = isUserAdminSync(user);

    if (isAdmin) {
      // Admin query: can view and manage all tracks
      try {
        const qAdmin = query(MUSIC_COLLECTION_REF, orderBy('addedAt', 'asc'));
        unsubAdmin = onSnapshot(
          qAdmin,
          (snapshot) => {
            if (isCancelled) return;
            const tracks: SongTrack[] = [];
            snapshot.forEach((d) => {
              const t = parseDoc(d);
              if (t) tracks.push(t);
            });
            onUpdate(tracks);
          },
          (err) => {
            const isOfflineError =
              err?.message?.includes('unavailable') ||
              err?.message?.includes('offline') ||
              (err as any)?.code === 'unavailable';
            if (!isOfflineError && onError) {
              try { onError(err); } catch {}
            }
          }
        );
      } catch (err: any) {
        if (onError) onError(err);
      }
    } else if (user && user.uid) {
      // Authenticated regular user: query own private tracks
      try {
        const qPrivate = query(MUSIC_COLLECTION_REF, where('ownerUid', '==', user.uid));
        unsubPrivate = onSnapshot(
          qPrivate,
          (snapshot) => {
            if (isCancelled) return;
            const list: SongTrack[] = [];
            snapshot.forEach((d) => {
              const t = parseDoc(d);
              if (t) list.push(t);
            });
            privateTracks = list;
            notifyCombined();
          },
          (err) => {
            const isOfflineError =
              err?.message?.includes('unavailable') ||
              err?.message?.includes('offline') ||
              (err as any)?.code === 'unavailable';
            if (!isOfflineError && onError) {
              try { onError(err); } catch {}
            }
          }
        );
      } catch (err: any) {
        if (onError) onError(err);
      }
    } else {
      notifyCombined();
    }
  };

  // Public query for all visitors and non-admins: only query isPublic == true
  try {
    const qPublic = query(MUSIC_COLLECTION_REF, where('isPublic', '==', true));
    unsubPublic = onSnapshot(
      qPublic,
      (snapshot) => {
        if (isCancelled) return;
        const list: SongTrack[] = [];
        snapshot.forEach((d) => {
          const t = parseDoc(d);
          if (t) list.push(t);
        });
        publicTracks = list;
        notifyCombined();
      },
      (err) => {
        const isOfflineError =
          err?.message?.includes('unavailable') ||
          err?.message?.includes('offline') ||
          (err as any)?.code === 'unavailable';
        if (!isOfflineError && onError) {
          try { onError(err); } catch {}
        }
      }
    );
  } catch (err: any) {
    if (onError) onError(err);
  }

  // React to auth changes dynamically
  const authUnsub = auth.onAuthStateChanged((user) => {
    setupAuthSubscriptions(user);
  });

  return () => {
    isCancelled = true;
    if (unsubPublic) unsubPublic();
    if (unsubPrivate) unsubPrivate();
    if (unsubAdmin) unsubAdmin();
    authUnsub();
  };
}

/**
 * Converts a File into Base64 string safely
 */
function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const res = reader.result as string;
      resolve(res);
    };
    reader.onerror = (err) => reject(err);
    reader.readAsDataURL(file);
  });
}

/**
 * Uploads an audio file by chunking it into subcollection documents in Firestore
 * and simultaneously caching it in IndexedDB for instant 0ms local playback.
 */
export async function saveAudioFileTrack(
  file: File,
  metadata: {
    title: string;
    artist: string;
    duration?: string;
    uploadedBy: 'novio' | 'novia';
    notes?: string;
  },
  onProgress?: (progressPercent: number) => void
): Promise<SongTrack> {
  const trackId = `track-${Date.now()}-${Math.random().toString(36).substring(2, 7)}`;
  const currentUid = auth.currentUser?.uid || '';
  if (onProgress) onProgress(10);

  // 1. Read file as Base64
  const dataUrl = await fileToBase64(file);
  if (onProgress) onProgress(30);

  // 2. Cache immediately in IndexedDB with owner-scoped key for instant zero-latency playback
  const cacheKey = getAudioCacheKey({ id: trackId, isPrivate: false, ownerUid: currentUid });
  try {
    await set(cacheKey, dataUrl);
  } catch (idbErr) {
    console.warn('IndexedDB caching warning:', idbErr);
  }
  if (onProgress) onProgress(45);

  // 3. Chunk the Base64 data for Firestore
  const totalLength = dataUrl.length;
  const totalChunks = Math.ceil(totalLength / CHUNK_SIZE);

  // 4. Save metadata document in music_tracks
  const trackRef = doc(db, 'music_tracks', trackId);
  const trackDocData = {
    id: trackId,
    title: metadata.title.trim(),
    artist: metadata.artist.trim() || 'Nuestra Canción',
    duration: metadata.duration || '3:30',
    uploadedBy: metadata.uploadedBy,
    addedAt: new Date().toISOString(),
    notes: metadata.notes?.trim() || '',
    storageType: 'cloud_chunks',
    totalChunks,
    mimeType: file.type || 'audio/mpeg',
    fileSize: file.size,
    isDefault: false,
    url: '', // Clean empty string to ensure metadata doc stays tiny (< 1KB)
    ownerUid: currentUid,
    isPublic: true,
    isPrivate: false,
  };

  await setDoc(trackRef, trackDocData);
  if (onProgress) onProgress(60);

  // 5. Upload chunks in parallel batches to music_tracks/{trackId}/chunks/{index}
  const chunkPromises: Promise<void>[] = [];
  for (let i = 0; i < totalChunks; i++) {
    const start = i * CHUNK_SIZE;
    const end = Math.min(start + CHUNK_SIZE, totalLength);
    const chunkData = dataUrl.substring(start, end);
    const chunkRef = doc(db, 'music_tracks', trackId, 'chunks', String(i));
    chunkPromises.push(
      setDoc(chunkRef, {
        index: i,
        data: chunkData,
      })
    );
  }

  await Promise.all(chunkPromises);
  if (onProgress) onProgress(100);

  // Return the created SongTrack
  return {
    ...trackDocData,
    url: dataUrl, // Local device has the full data URL ready to play
  } as SongTrack;
}

/**
 * Saves a direct URL song track (e.g. streaming link, preset)
 */
export async function saveUrlSongTrack(
  track: Omit<SongTrack, 'id' | 'addedAt'>
): Promise<SongTrack> {
  const trackId = `track-${Date.now()}-${Math.random().toString(36).substring(2, 7)}`;
  const trackRef = doc(db, 'music_tracks', trackId);

  const currentUid = auth.currentUser?.uid || '';
  const fullTrack: SongTrack = {
    ...track,
    id: trackId,
    addedAt: new Date().toISOString(),
    storageType: track.url.startsWith('synth') ? 'synth' : 'url',
    isDefault: false,
    ownerUid: track.ownerUid || currentUid,
    isPublic: track.isPublic !== undefined ? track.isPublic : true,
    isPrivate: track.isPrivate !== undefined ? track.isPrivate : false,
  };

  await setDoc(trackRef, fullTrack);
  return fullTrack;
}

/**
 * Resolves the playable audio URL for a given track:
 * - If preset or direct URL, returns track.url directly.
 * - If cloud chunks:
 *   1. Checks in-memory object URL cache.
 *   2. Checks IndexedDB (`couple_audio_${track.id}`).
 *   3. If not found locally, fetches all chunks from Firestore, reassembles them,
 *      caches in IndexedDB, and creates a Blob Object URL.
 */
export async function getPlayableAudioUrl(track: SongTrack): Promise<string> {
  if (!track) return '';

  // 1. Determine if the track is private
  const isPrivate = Boolean(track.isPrivate || (track.isPublic === false));

  // 2. If private, verify that the current user is the owner or an admin
  if (isPrivate) {
    const currentUid = auth.currentUser?.uid;
    const isOwner = Boolean(currentUid && track.ownerUid && currentUid === track.ownerUid);
    const isAdmin = isUserAdminSync(auth.currentUser);
    if (!isOwner && !isAdmin) {
      console.warn('Unauthorized audio access attempt for private track:', track.id);
      return '';
    }
  }

  // 3. Authorization verified (or track is public): check for direct URL or synthesizer
  if (
    track.url &&
    isSafeMediaUrl(track.url) &&
    (track.url.startsWith('http') ||
      track.url.startsWith('synth') ||
      track.url.startsWith('blob:') ||
      track.url.startsWith('data:'))
  ) {
    return track.url;
  }

  const cacheKey = getAudioCacheKey(track);

  // Check in-memory cache
  if (objectUrlCache.has(cacheKey)) {
    return objectUrlCache.get(cacheKey)!;
  }

  // Check IndexedDB
  try {
    const cachedDataUrl = await get<string>(cacheKey);
    if (cachedDataUrl) {
      objectUrlCache.set(cacheKey, cachedDataUrl);
      return cachedDataUrl;
    }
  } catch (idbErr) {
    console.warn('Could not read from IndexedDB:', idbErr);
  }

  // Fetch chunks from Firestore subcollection
  try {
    const chunksCollectionRef = collection(db, 'music_tracks', track.id, 'chunks');
    const chunksSnap = await getDocs(chunksCollectionRef);

    if (chunksSnap.empty) {
      console.warn('No chunks found for track:', track.id);
      return '';
    }

    // Sort chunks by index
    const sortedDocs = chunksSnap.docs
      .map((d) => d.data() as { index: number; data: string })
      .sort((a, b) => a.index - b.index);

    const reassembledBase64 = sortedDocs.map((c) => c.data).join('');

    // Cache in IndexedDB for subsequent zero-delay playback
    try {
      await set(cacheKey, reassembledBase64);
    } catch {}

    objectUrlCache.set(cacheKey, reassembledBase64);
    return reassembledBase64;
  } catch (err) {
    console.error('Error fetching audio chunks from Firestore:', err);
    return '';
  }
}

/**
 * Deletes a music track from Firestore, its chunks subcollection, and local caches.
 */
export async function deleteMusicTrackFromDb(trackId: string): Promise<void> {
  try {
    // 1. Delete all chunks in subcollection
    const chunksCollectionRef = collection(db, 'music_tracks', trackId, 'chunks');
    const chunksSnap = await getDocs(chunksCollectionRef);
    const deletePromises = chunksSnap.docs.map((d) => deleteDoc(d.ref));
    await Promise.all(deletePromises);

    // 2. Delete parent track doc
    const trackRef = doc(db, 'music_tracks', trackId);
    await deleteDoc(trackRef);

    // 3. Clear from IndexedDB and memory cache
    try {
      await del(getAudioCacheKey({ id: trackId, isPrivate: false }));
      await del(`couple_audio_${trackId}`);
      const currentUid = auth.currentUser?.uid;
      if (currentUid) {
        await del(getAudioCacheKey({ id: trackId, isPrivate: true, ownerUid: currentUid }));
      }
    } catch {}
    if (objectUrlCache.has(trackId)) {
      const existing = objectUrlCache.get(trackId);
      if (existing?.startsWith('blob:')) {
        URL.revokeObjectURL(existing);
      }
      objectUrlCache.delete(trackId);
    }
  } catch (err) {
    console.error('Error deleting music track from Firestore:', err);
    throw err;
  }
}
